import styled from 'styled-components'
import { resolveVisibility, evaluateCondition } from "../../../../common/model/condition/evaluate"
import useTranslation from '../../hooks/useTranslation'
import { MdEdit } from 'react-icons/md'
import FormElem from '../cardView/FormElem'
import { useStore } from '../pageView/Page'

const Card = styled.div`
    position: relative;
    background: ${({ theme }) => theme.card.background};
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 1rem;
    padding: 0.75rem 0.75rem 0.6rem;
    box-shadow: ${({ theme }) => theme.card.lightBoxShadow};
`

const EditIcon = styled.button`
    position: absolute;
    top: 0.6rem;
    right: 0.6rem;
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.font.label};
    font-size: 1.1rem;
    padding: 0;
    line-height: 1;
    &:hover { color: ${({ theme }) => theme.font.color}; }
`

const Body = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding-right: ${({ $hasEdit }) => $hasEdit ? '1.5rem' : '0'};
`

const Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    flex: ${({ $flex }) => $flex ?? 1};
    min-width: 0;
    align-items: ${({ $flex }) => $flex === 0 ? 'flex-end' : 'flex-start'};
`

const FieldSku = styled.span`
    font-size: 0.95rem;
    color: ${({ theme }) => theme.font.label};
    font-weight: 500;
`

const FieldName = styled.span`
    font-size: 0.92rem;
    font-weight: 600;
    color: ${({ theme }) => theme.font.color};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const FieldLocation = styled.span`
    font-size: 0.78rem;
    color: ${({ theme }) => theme.font.label};
`

const FieldText = styled.span`
    font-size: 0.82rem;
    color: ${({ theme }) => theme.font.color};
`

const QtyBox = styled.div`
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 0.5rem;
    padding: 0.25rem 0.6rem;
    text-align: center;
    min-width: 3.5rem;
`

const QtyNum = styled.div`
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1.1;
    color: ${({ theme }) => theme.font.color};
`

const QtyUom = styled.div`
    font-size: 0.7rem;
    color: ${({ theme }) => theme.font.label};
    text-transform: uppercase;
`

const DoneBadge = styled.span`
    font-size: 0.72rem;
    font-weight: 700;
    color: ${({ theme }) => theme.btn.primary};
`

const LocationPickerWrapper = styled.div`
    margin-top: 0.5rem;
`

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.5rem;
`

const ActionButton = styled.button`
    padding: 0.3rem 1rem;
    font-size: 0.82rem;
    font-weight: 600;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    background: ${({ theme, $variant }) => $variant === 'secondary' ? theme.btn.secondary : theme.btn.primary};
    color: ${({ theme }) => theme.btn.font};
    &:hover:not(:disabled) { opacity: 0.88; }
    &:disabled { opacity: 0.38; cursor: not-allowed; }
`

const renderElement = (el, line, isDone) => {
    if (el.type === 'quantity' || el.type === 'quantitySplit') {
        const value = el.type === 'quantity'
            ? line[el.value]
            : (line[el.actual] ?? line[el.planned])
        return (
            <QtyBox key={el.value ?? el.planned}>
                <QtyNum>{value}</QtyNum>
                {el.decorator && <QtyUom>{line[el.decorator]}</QtyUom>}
            </QtyBox>
        )
    }

    const raw = line[el.value]
    const text = [el.prefix, raw, el.suffix].filter(Boolean).join(' ')

    switch (el.scale) {
        case 'title':    return <FieldName key={el.value}>{raw}</FieldName>
        case 'location': return raw ? <FieldLocation key={el.value}>{text}</FieldLocation> : null
        case 'normal':   return <FieldSku key={el.value}>{text}</FieldSku>
        default:         return <FieldText key={el.value}>{text}</FieldText>
    }
}

const isButtonVisible = (btn, isDone) =>
    resolveVisibility(btn, { values: {}, context: { done: isDone } }) === 'show'

// Item cards repeat per line, so their test ids carry the row number the way the
// rest of the app does (`stockAdjustment.lines.1.save`). The action comes from
// the last segment of the config id — `whPickOrder.toPick.pick` -> `pick` —
// which keeps the id in the model rather than the component.
const actionOf = (btn) => String(btn.id ?? '').split('.').pop()

const ProcessItemCard = ({ line, section, mode, onAction, onOpenDetail, allLines, linesField, completed, testId }) => {
    const { t } = useTranslation()
    const isDone = mode === 'done'

    const card = section?.itemCard ?? {}

    const isEnabled = (btn) =>
        !btn.enabledWhen || evaluateCondition(btn.enabledWhen, { values: line, context: { done: isDone } })

    const visibleButtons = (section.buttons || []).filter(btn => isButtonVisible(btn, isDone))

    const lineIndex = card.locationPicker && allLines
        ? allLines.findIndex(l => l.id === line.id)
        : -1

    return (
        <Card data-testid={testId}>
            {!completed && card.showDetail !== false && (
                <EditIcon data-testid={testId && `${testId}.edit`} onClick={() => onOpenDetail(line.id)}>
                    <MdEdit />
                </EditIcon>
            )}

            <Body $hasEdit={!completed}>
                {isDone && <DoneBadge>✓</DoneBadge>}
                {(card.columns ?? []).map((col, ci) => (
                    <Column key={ci} $flex={col.weight}>
                        {(col.elements || []).map(el => renderElement(el, line, isDone))}
                    </Column>
                ))}
            </Body>

            {card.locationPicker && !isDone && !completed && lineIndex >= 0 && linesField && (
                <LocationPickerWrapper>
                    <FormElem
                        item={{
                            type: 'connect',
                            id: card.locationPicker.id,
                            displayField: card.locationPicker.displayField,
                            label: card.locationPicker.label,
                            entity: 'location',
                            modalType: 'radioGroup',
                            radioGroups: card.locationPicker.radioGroups ?? [],
                            modalTitle: card.locationPicker.modalTitle,
                            selectButtonKey: card.locationPicker.selectButtonKey,
                            valueMap: card.locationPicker.valueMap,
                            persistValue: true,
                            options: { withHeaders: false },
                            fgMargin: '0'
                        }}
                        lineId={line.id}
                        useStore={useStore}
                        path={[linesField, lineIndex]}
                        validated={false}
                        section={{ meta: {} }}
                    />
                </LocationPickerWrapper>
            )}

            {!completed && visibleButtons.length > 0 && (
                <Footer>
                    {visibleButtons.map((btn, i) => (
                        <ActionButton
                            key={i}
                            type="button"
                            $variant={btn.variant}
                            data-testid={testId && `${testId}.${actionOf(btn)}`}
                            disabled={!isEnabled(btn)}
                            onClick={() => onAction(btn.effects, line.id)}
                        >
                            {t(btn.label)}
                        </ActionButton>
                    ))}
                </Footer>
            )}
        </Card>
    )
}

export default ProcessItemCard
